<script setup lang="ts">
import { ref, watch } from 'vue'
import { Strategy } from '@/types'
import { strategiesApi } from '@/api/client'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/UserStore'
import { AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'

const { t } = useI18n()
const user = useUserStore()

const props = defineProps<{
    strategy: Strategy
}>()

const emit = defineEmits(['success', 'error'])

const strategy = ref(props.strategy)

let absValue: number = Math.abs(strategy.value.values.value)
let higherOrLower = ref(strategy.value.values.value > 0 ? 'higher' : 'lower')

watch(() => strategy.value.values.rounding_type, (type) => {
    switch (type) {
        case 'point_nine':
            strategy.value.values.rounding_value = 0.9
            break
        case 'nine':
            strategy.value.values.rounding_value = 9
            break
        default:
            break
    }
})

const isToggling = ref(false)

/**
 * A disabled strategy keeps its configuration — it is simply never applied, so the
 * suggested price stays the raw market figure until it is switched back on.
 */
const onToggle = async (enabled: boolean) => {
    isToggling.value = true

    try {
        const response = await strategiesApi.toggle(strategy.value.category.id, enabled)

        strategy.value.enabled = enabled
        emit('success', response.data)
    } catch (error: any) {
        emit('error', error.response.data.message)
    } finally {
        isToggling.value = false
    }
}

const onSubmitVariation = async (_event: Event) => {
    try {
        const response = await strategiesApi.update(strategy.value.category.id, {
            type: strategy.value.type,
            values: {
                rounding_value: Number(strategy.value.values.rounding_value),
                rounding_type: strategy.value.values.rounding_type,
                min_price_percentage: Number(strategy.value.values.min_price_percentage),
                max_price_percentage: Number(strategy.value.values.max_price_percentage),
            },
        })

        emit('success', response.data)
    } catch (error: any) {
        emit('error', error.response.data.message)
    }
}

const onSubmitMarket = async (_event: Event) => {
    try {
        const response = await strategiesApi.update(strategy.value.category.id, {
            type: strategy.value.type,
            values: {
                value: higherOrLower.value === 'higher' ? absValue : -absValue,
                comparison: strategy.value.values.comparison,
                market_price: strategy.value.values.market_price,
                margin: Number(strategy.value.values.margin),
                validate_against_next_gen: strategy.value.values.validate_against_next_gen,
            },
        })

        emit('success', response.data)
    } catch (error: any) {
        emit('error', error.response.data.message)
    }
}
</script>

<template>
    <AccordionItem :value="strategy.id.toString()">
        <div class="flex items-center gap-4">
            <AccordionTrigger class="text-base" :class="{ 'text-muted-foreground': !strategy.enabled }">
                {{ t('strategies.card.title', { category: strategy.category.name }) }}
            </AccordionTrigger>
            <Switch
                :model-value="strategy.enabled"
                :disabled="isToggling"
                :aria-label="t('strategies.card.toggle', { category: strategy.category.name })"
                @update:model-value="onToggle"
            />
        </div>
        <AccordionContent class="overflow-x-auto" :class="{ 'opacity-50': !strategy.enabled }">
            <form v-if="strategy.type === 'variation'" @submit.prevent="onSubmitVariation">
                <div class="leading-10 text-muted-foreground">
                    {{ t('strategies.card.minSentence') }}

                    <div class="inline-flex w-24 mx-4 relative">
                        <span class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground"
                            >%</span
                        >
                        <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground"
                            >-</span
                        >
                        <Input
                            v-model="strategy.values.min_price_percentage"
                            type="text"
                            class="px-8"
                        />
                    </div>

                    {{ t('strategies.card.maxSentence') }}

                    <div class="inline-flex w-24 mx-4 relative">
                        <span class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground"
                            >%</span
                        >
                        <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground"
                            >+</span
                        >
                        <Input
                            v-model="strategy.values.max_price_percentage"
                            type="text"
                            class="px-8"
                        />
                    </div>

                    {{ t('strategies.card.roundSentence') }}

                    <div class="inline-flex w-36 mx-4">
                        <Select v-model="strategy.values.rounding_type">
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="fixed">{{ user.extra?.catalog_currency }}</SelectItem>
                                <SelectItem value="nine">9 {{ user.extra?.catalog_currency }}</SelectItem>
                                <SelectItem value="point_nine">.9 {{ user.extra?.catalog_currency }}</SelectItem>
                                <SelectItem value="exact_amount">None</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div
                        v-show="strategy.values.rounding_type === 'percentage' || strategy.values.rounding_type === 'fixed'"
                        class="inline-flex"
                    >
                        {{ t('strategies.card.of') }}

                        <div class="inline-flex w-20 mx-4 relative">
                            <span
                                v-if="strategy.values.rounding_type === 'percentage'"
                                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground"
                                >%</span
                            >
                            <span
                                v-if="strategy.values.rounding_type !== 'percentage'"
                                class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground"
                                >{{ user.extra?.catalog_currency_symbol }}</span
                            >
                            <Input
                                v-model="strategy.values.rounding_value"
                                type="text"
                                class="pr-8"
                            />
                        </div>
                    </div>
                </div>

                <div class="flex mt-4">
                    <Button type="submit">
                        {{ t('strategies.card.apply') }}
                    </Button>
                </div>
            </form>

            <form v-if="strategy.type === 'market'" @submit.prevent="onSubmitMarket">
                <div class="leading-10 text-muted-foreground">
                    I want the suggested price to be

                    <div class="inline-flex w-12 mx-4 relative">
                        <Input
                            v-model="absValue"
                            type="text"
                            class="px-2.5"
                        />
                    </div>

                    <div class="inline-flex w-24 mx-4">
                        <Select v-model="strategy.values.comparison">
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="percentage">%</SelectItem>
                                <SelectItem value="fixed">{{ user.extra?.catalog_currency_symbol }}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div class="inline-flex w-32 mx-4">
                        <Select v-model="higherOrLower">
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="higher">higher</SelectItem>
                                <SelectItem value="lower">lower</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    than the

                    <div class="inline-flex w-52 mx-4">
                        <Select v-model="strategy.values.market_price">
                            <SelectTrigger>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="median">median market price</SelectItem>
                                <SelectItem value="max">max market price</SelectItem>
                                <SelectItem value="min">min market price</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    but higher than

                    <div class="inline-flex w-20 mx-4 relative">
                        <span class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-muted-foreground"
                            >%</span
                        >
                        <Input
                            v-model="strategy.values.margin"
                            type="text"
                            class="pr-8"
                        />
                    </div>

                    margin
                </div>

                <div v-if="strategy.category.slug === 'smartphone'" class="flex items-center gap-2 mb-4 mt-4">
                    <Checkbox
                        :id="strategy.id + '_validate_against_next_gen'"
                        v-model:checked="strategy.values.validate_against_next_gen"
                    />
                    <label :for="strategy.id + '_validate_against_next_gen'" class="text-sm text-muted-foreground"
                        >Keep brand new device pricing below next-gen model (n+1) brand new device pricing (where
                        possible)</label
                    >
                </div>

                <div class="flex mt-4">
                    <Button type="submit">
                        {{ t('strategies.card.apply') }}
                    </Button>
                </div>
            </form>

            <div v-if="strategy.type === 'revendo'" class="text-muted-foreground">
                <div class="text-lg mt-4 text-foreground">
                    When <code class="text-base font-semibold">stock_rfs >= 10</code> ...
                </div>

                <div class="leading-6 divide-y divide-border">
                    <div class="py-4">
                        ... and <code class="text-sm font-semibold mx-2 text-foreground">days_till_sold_out >= 180</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">days_since_price_change >= 14</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_promotion is false</code> I want the
                        suggested price to be
                        <code class="text-sm font-semibold mx-2 text-foreground"
                            >1 {{ user.extra?.catalog_currency_symbol }} lower than the min market price</code
                        >
                        but <code class="text-sm font-semibold mx-2 text-foreground">higher than 25% margin</code>
                    </div>

                    <div class="py-4">
                        ... and
                        <code class="text-sm font-semibold mx-2 text-foreground">80 <= days_till_sold_out < 180</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_sales_accelerated is false</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">days_since_price_change >= 30</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_promotion is false</code> I want the
                        suggested price to be
                        <code class="text-sm font-semibold mx-2 text-foreground"
                            >1 {{ user.extra?.catalog_currency_symbol }} lower than the min market price</code
                        >
                        but <code class="text-sm font-semibold mx-2 text-foreground">higher than 30% margin</code>
                    </div>

                    <div class="py-4">
                        ... and
                        <code class="text-sm font-semibold mx-2 text-foreground">80 <= days_till_sold_out < 180</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_sales_accelerated is false</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">14 <= days_since_price_change < 30</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_promotion is false</code> I want the
                        suggested price to be
                        <code class="text-sm font-semibold mx-2 text-foreground"
                            >1 {{ user.extra?.catalog_currency_symbol }} lower than the min market price</code
                        >
                        but <code class="text-sm font-semibold mx-2 text-foreground">higher than 33% margin</code>
                    </div>

                    <div class="py-4">
                        ... and
                        <code class="text-sm font-semibold mx-2 text-foreground">30 <= days_till_sold_out < 80</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_sales_accelerated is false</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">days_since_price_change >= 30</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_promotion is false</code> I want the
                        suggested price to be
                        <code class="text-sm font-semibold mx-2 text-foreground"
                            >1 {{ user.extra?.catalog_currency_symbol }} lower than the min market price</code
                        >
                        but <code class="text-sm font-semibold mx-2 text-foreground">higher than 34% margin</code>
                    </div>

                    <div class="py-4">
                        ... and
                        <code class="text-sm font-semibold mx-2 text-foreground">30 <= days_till_sold_out < 80</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_sales_accelerated is false</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">14 <= days_since_price_change < 30</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_promotion is false</code> I want the
                        suggested price to be
                        <code class="text-sm font-semibold mx-2 text-foreground"
                            >1 {{ user.extra?.catalog_currency_symbol }} lower than the min market price</code
                        >
                        but <code class="text-sm font-semibold mx-2 text-foreground">higher than 35% margin</code>
                    </div>
                </div>

                <div class="text-lg mt-12 text-foreground">
                    When <code class="text-base font-semibold">stock_rfs < 10</code> ...
                </div>

                <div class="leading-6 divide-y divide-border">
                    <div class="py-4">
                        ... and <code class="text-sm font-semibold mx-2 text-foreground">days_till_sold_out >= 180</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">days_since_price_change >= 14</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_promotion is false</code> I want the
                        suggested price to be
                        <code class="text-sm font-semibold mx-2 text-foreground"
                            >1 {{ user.extra?.catalog_currency_symbol }} lower than the min market price</code
                        >
                        but <code class="text-sm font-semibold mx-2 text-foreground">higher than 28% margin</code>
                    </div>

                    <div class="py-4">
                        ... and
                        <code class="text-sm font-semibold mx-2 text-foreground">80 <= days_till_sold_out < 180</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_sales_accelerated is false</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">days_since_price_change >= 30</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_promotion is false</code> I want the
                        suggested price to be
                        <code class="text-sm font-semibold mx-2 text-foreground"
                            >1 {{ user.extra?.catalog_currency_symbol }} lower than the min market price</code
                        >
                        but <code class="text-sm font-semibold mx-2 text-foreground">higher than 31% margin</code>
                    </div>

                    <div class="py-4">
                        ... and
                        <code class="text-sm font-semibold mx-2 text-foreground">80 <= days_till_sold_out < 180</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_sales_accelerated is false</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">14 <= days_since_price_change < 30</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_promotion is false</code> I want the
                        suggested price to be
                        <code class="text-sm font-semibold mx-2 text-foreground"
                            >1 {{ user.extra?.catalog_currency_symbol }} lower than the min market price</code
                        >
                        but <code class="text-sm font-semibold mx-2 text-foreground">higher than 34% margin</code>
                    </div>

                    <div class="py-4">
                        ... and
                        <code class="text-sm font-semibold mx-2 text-foreground">30 <= days_till_sold_out < 80</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_sales_accelerated is false</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">days_since_price_change >= 30</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_promotion is false</code> I want the
                        suggested price to be
                        <code class="text-sm font-semibold mx-2 text-foreground"
                            >1 {{ user.extra?.catalog_currency_symbol }} lower than the min market price</code
                        >
                        but <code class="text-sm font-semibold mx-2 text-foreground">higher than 35% margin</code>
                    </div>

                    <div class="py-4">
                        ... and
                        <code class="text-sm font-semibold mx-2 text-foreground">30 <= days_till_sold_out < 80</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_sales_accelerated is false</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">14 <= days_since_price_change < 30</code> and
                        <code class="text-sm font-semibold mx-2 text-foreground">is_promotion is false</code> I want the
                        suggested price to be
                        <code class="text-sm font-semibold mx-2 text-foreground"
                            >1 {{ user.extra?.catalog_currency_symbol }} lower than the min market price</code
                        >
                        but <code class="text-sm font-semibold mx-2 text-foreground">higher than 36% margin</code>
                    </div>
                </div>

                <div v-if="strategy.category.slug === 'smartphone'" class="flex items-center gap-2 mb-4 mt-4">
                    <Checkbox
                        checked
                        disabled
                    />
                    <label class="text-sm text-muted-foreground"
                        >Keep brand new device pricing below next-gen model (n+1) brand new device pricing (where
                        possible)</label
                    >
                </div>
            </div>
        </AccordionContent>
    </AccordionItem>
</template>
